import type { FileTypeResult } from 'file-type';

export const BLOB_FILE_TYPE_SNIFF_BYTES = 4100;
const DEFAULT_BLOB_MIME_TYPE = 'application/octet-stream';

const MIME_BY_EXTENSION: Record<string, string> = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  htm: 'text/html',
  html: 'text/html',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  json: 'application/json',
  pdf: 'application/pdf',
  png: 'image/png',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  webp: 'image/webp',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xml: 'application/xml',
  zip: 'application/zip',
};

export async function sniffBlobFileType(
  blob: Blob
): Promise<FileTypeResult | undefined> {
  const { fileTypeFromBuffer } = await import('file-type');
  const header = await blob.slice(0, BLOB_FILE_TYPE_SNIFF_BYTES).arrayBuffer();
  return fileTypeFromBuffer(new Uint8Array(header));
}

export function mimeTypeFromFilename(
  filename: string | null | undefined
): string | undefined {
  const ext = filename?.split('.').pop()?.trim().toLowerCase();
  return ext ? MIME_BY_EXTENSION[ext] : undefined;
}

export function resolveBlobMimeTypeHint(
  mimeType: string | null | undefined,
  filename: string | null | undefined
): string | undefined {
  const explicit = mimeType?.trim();
  if (explicit && explicit !== DEFAULT_BLOB_MIME_TYPE) {
    return explicit;
  }
  return mimeTypeFromFilename(filename) ?? explicit;
}

export async function resolveBlobMimeType(
  blob: Blob,
  filename?: string | null
): Promise<string> {
  const explicit = blob.type?.trim();
  if (explicit && explicit !== DEFAULT_BLOB_MIME_TYPE) {
    return explicit;
  }

  try {
    const fileType = await sniffBlobFileType(blob);
    if (fileType?.mime) return fileType.mime;
  } catch {
    // Header sniffing is best effort; extension/default still gives a stable type.
  }

  return resolveBlobMimeTypeHint(explicit, filename) ?? DEFAULT_BLOB_MIME_TYPE;
}

export async function fileWithResolvedMimeType(file: File): Promise<File> {
  const mime = await resolveBlobMimeType(file, file.name);
  if (file.type === mime) return file;
  return new File([file], file.name, {
    type: mime,
    lastModified: file.lastModified,
  });
}
